import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  LocalizationProvider,
  DatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import toast from "react-hot-toast";
import SignatureCanvas from "react-signature-canvas";
import { MdWarning } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ReferralReferrals } from "components/Referrals/Referral";
import * as AnamneseStyled from "components/FirstConsult/components/Steps/Anamnese/styles";
import * as RecordStyled from "components/Maintenance/components/Steps/ClinicalRecord/styles";
import { postAnamnese } from "requests/Anamnese";
import { sendClinical } from "requests/Clinical";
import { loadOrcamento } from "requests/ClinicalBudget";
import { checkReferralNeeded } from "requests/ReferralCheck";
import { sendSchedule } from "requests/SendSchedule";
import { getTimeTable } from "requests/Time";
import { questions, Question } from "shared/data/question";
import { ROUTER } from "shared/constants/router";
import { NeurologicalConditions } from "shared/mock";
import { useAuth } from "shared/providers/auth";
import { useClinical } from "shared/providers/Clinical";
import { IAnamnese, IAnamneseData } from "shared/types/Anamnese";
import { IBudgetItem } from "shared/types/ClinicalBudget";
import { ISendSchedule } from "shared/types/SendSchedule";
import { HeadConsult } from "./Head";

const ANAMNESIS_OPTIONS: IAnamnese[] = [
  { label: "Asma", identify: "OCQ1" },
  { label: "Anemia", identify: "OCQ2" },
  { label: "Febre reumática", identify: "OCQ3" },
  { label: "Epilepsia", identify: "OCQ4" },
  { label: "Distúrbios auditivos", identify: "OCQ5" },
  { label: "Herpes", identify: "OCQ6" },
  { label: "Hepatite", identify: "OCQ7" },
  { label: "Diabete", identify: "OCQ8" },
  { label: "HIV", identify: "OCQ9" },
  { label: "Gestante", identify: "OCQ10" },
  { label: "Fumante", identify: "OCQ11" },
  { label: "Hipertensão", identify: "OCQ12" },
];

const APPOINTMENT_INTERVALS = [15, 30, 45, 60, 90, 120];
const SIGNATURE_HEIGHT = 260;

interface AvailableDates {
  [date: string]: string[];
}

interface TimeTableData {
  bestDay: string;
  availableDates: AvailableDates;
}

const convertDateToAmerican = (dateString: string): string => {
  const year = dateString.slice(0, 4);
  const month = dateString.slice(4, 6);
  const day = dateString.slice(6, 8);

  return `${month}/${day}/${year}`;
};

const getBudgetItemDescription = (item: IBudgetItem): string => {
  const description: string[] = [];

  if (item.area) description.push(item.area);
  if (item.elemento) description.push(`Elemento ${item.elemento}`);
  if (item.faces) description.push(`Faces ${item.faces}`);
  if (item.obsPersonalizado) description.push(item.obsPersonalizado);

  return description.join(" | ");
};

const isLaunchCompleted = (item: IBudgetItem): boolean =>
  Number(item.realizado || 0) >= 2;

const isLaunchScheduled = (item: IBudgetItem): boolean =>
  Number(item.realizado || 0) === 1 || Number(item.agendaId || 0) > 0;

const Finalize: React.FC = () => {
  const { patientObj } = useClinical();
  const { user } = useAuth();
  const navigate = useNavigate();

  const hasBudgetFlow = Number(patientObj?.dentista_orcamento) === 1;
  const hasAnamnesisStep =
    hasBudgetFlow && Number(patientObj?.aname_id || 0) <= 0;
  const currentProcedureName = patientObj?.proc_name || "Atendimento clínico";
  const currentScheduleDate = patientObj?.dt_start
    ? patientObj.dt_start.slice(0, 16)
    : "";
  const currentDentistId = patientObj?.idDent_rel || patientObj?.idDent || 0;
  const attendanceBudgetId =
    Number(patientObj?.clinical_budget_id || 0) > 0
      ? Number(patientObj?.clinical_budget_id)
      : null;

  const [step, setStep] = React.useState<number>(hasAnamnesisStep ? 0 : 1);
  // A observação clínica só é exibida (e, portanto, exigida) na etapa da ficha
  // clínica do fluxo de orçamento. Em fluxos sem orçamento o campo nem aparece.
  const showClinicalObservation =
    hasBudgetFlow && (!hasAnamnesisStep || step === 1);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isBtnFixedVisible, setIsBtnFixedVisible] =
    React.useState<boolean>(true);
  const [clinicalObservation, setClinicalObservation] =
    React.useState<string>("");

  const [selectedOptions, setSelectedOptions] = React.useState<IAnamnese[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] =
    React.useState<number>(0);
  const [answers, setAnswers] = React.useState<{ [key: string]: string }>({});
  const [observations, setObservations] = React.useState<{
    [key: string]: string;
  }>({});
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const [selectedNeurologicalConditions, setSelectedNeurologicalConditions] =
    React.useState<string[]>([]);
  const [otherNeurologicalCondition, setOtherNeurologicalCondition] =
    React.useState<string>("");
  const [hasNeurologicalCondition, setHasNeurologicalCondition] =
    React.useState<boolean>(false);

  const [signatureDoctor, setSignatureDoctor] = React.useState<string | null>(
    null
  );
  const [signaturePatient, setSignaturePatient] = React.useState<string | null>(
    null
  );

  const sigCanvasPatient = React.useRef<SignatureCanvas>(null);
  const sigCanvasDentist = React.useRef<SignatureCanvas>(null);
  const sigContainerPatient = React.useRef<HTMLDivElement>(null);
  const sigContainerDentist = React.useRef<HTMLDivElement>(null);

  const [showReferralDialog, setShowReferralDialog] =
    React.useState<boolean>(false);
  const [showMandatoryReferral, setShowMandatoryReferral] =
    React.useState<boolean>(false);
  const [referralCompleted, setReferralCompleted] =
    React.useState<boolean>(false);
  const referralCompletedRef = React.useRef<boolean>(false);
  const [preSelectedProcedure, setPreSelectedProcedure] = React.useState<
    "profilaxia" | "raio_x" | "avaliacao" | null
  >(null);
  const [referralData, setReferralData] = React.useState<{
    needsProfilaxia: boolean;
    needsPanoramica: boolean;
    needsAvaliacao: boolean;
    lastProfilaxiaDate: string | null;
    lastPanoramicaDate: string | null;
  } | null>(null);

  const [loadingBudgetItems, setLoadingBudgetItems] =
    React.useState<boolean>(false);
  const [budgetItems, setBudgetItems] = React.useState<IBudgetItem[]>([]);
  const [budgetLoadError, setBudgetLoadError] = React.useState<string>("");
  const [scheduleNextAppointment, setScheduleNextAppointment] =
    React.useState<boolean>(false);
  const [selectedBudgetItemId, setSelectedBudgetItemId] =
    React.useState<string>("");
  const [selectedInterval, setSelectedInterval] =
    React.useState<string>("30");
  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(null);
  const [selectedHour, setSelectedHour] = React.useState<string>("");
  const [availableHours, setAvailableHours] = React.useState<string[]>([]);
  const [timeTableData, setTimeTableData] = React.useState<TimeTableData | null>(
    null
  );
  const [loadingTimeTable, setLoadingTimeTable] =
    React.useState<boolean>(false);
  const [openAfterDay20, setOpenAfterDay20] = React.useState<boolean>(false);

  const answeredYes = React.useMemo(
    () => questions.filter((question) => answers[question.questionText] === "sim"),
    [answers]
  );
  const answeredNo = React.useMemo(
    () => questions.filter((question) => answers[question.questionText] === "não"),
    [answers]
  );

  const anamnesisPayload = React.useMemo(() => {
    return [
      ...selectedOptions.map((item) => ({
        id: item.identify,
        option: 1,
      })),
      ...answeredYes.map((item) => ({
        id: item.questionIdentifier,
        description: observations[item.questionText] || "",
        option: 1,
      })),
      ...answeredNo.map((item) => ({
        id: item.questionIdentifier,
        option: 0,
      })),
      ...(hasNeurologicalCondition
        ? [
            {
              id: "NEURO",
              option: 1,
              description: selectedNeurologicalConditions.join(", "),
              otherConditions: otherNeurologicalCondition || "",
              hasSpecialNeeds: true,
            },
          ]
        : []),
    ];
  }, [
    answeredNo,
    answeredYes,
    hasNeurologicalCondition,
    observations,
    otherNeurologicalCondition,
    selectedNeurologicalConditions,
    selectedOptions,
  ]);

  const selectedBudgetItem = React.useMemo(
    () => budgetItems.find((item) => item.id === selectedBudgetItemId) || null,
    [budgetItems, selectedBudgetItemId]
  );

  const availableBudgetItems = React.useMemo(() => {
    return budgetItems.filter((item) => {
      const isCurrentLaunch =
        Number(patientObj?.clinical_launch_id || 0) > 0 &&
        Number(item.id) === Number(patientObj?.clinical_launch_id);

      return (
        !isLaunchCompleted(item) &&
        !isLaunchScheduled(item) &&
        !isCurrentLaunch
      );
    });
  }, [budgetItems, patientObj?.clinical_launch_id]);

  const handleOptionChange = React.useCallback((option: IAnamnese) => {
    setSelectedOptions((current) =>
      current.includes(option)
        ? current.filter((item) => item !== option)
        : [...current, option]
    );
  }, []);

  const handleAnswerChange = React.useCallback(
    (questionText: string, answer: string, isClearable: boolean = true) => {
      const currentIndex = questions.findIndex(
        (question) => question.questionText === questionText
      );

      if (isClearable) {
        const nextAnswers = { ...answers };
        const nextObservations = { ...observations };
        const nextErrors = { ...errors };

        for (let index = currentIndex + 1; index < questions.length; index += 1) {
          delete nextAnswers[questions[index].questionText];
          delete nextObservations[questions[index].questionText];
          delete nextErrors[questions[index].questionText];
        }

        if (answer === "sim" && !observations[questionText]) {
          nextErrors[questionText] = "Observação é obrigatória";
        } else {
          delete nextErrors[questionText];
        }

        setAnswers(nextAnswers);
        setObservations(nextObservations);
        setErrors(nextErrors);
      }

      if (answer === "sim") {
        if (
          questionText === "A recuperação aconteceu como esperado?" ||
          questionText === "A recuperação ocorreu como esperado?"
        ) {
          setCurrentQuestionIndex(currentIndex + 1);
        } else {
          setCurrentQuestionIndex(currentIndex);
          setAnswers((current) => ({ ...current, [questionText]: answer }));
          return;
        }
      }

      if (questionText === "Cirurgia anterior?" && answer === "não") {
        setCurrentQuestionIndex(currentIndex + 3);
      } else {
        setCurrentQuestionIndex(currentIndex + 1);
      }

      if (questionText === "Fraturas ósseas?" && answer === "não") {
        setCurrentQuestionIndex(currentIndex + 2);
      }

      setAnswers((current) => ({ ...current, [questionText]: answer }));
    },
    [answers, errors, observations]
  );

  const handleObservationChange = React.useCallback(
    (questionText: string, observation: string) => {
      const currentIndex = questions.findIndex(
        (question) => question.questionText === questionText
      );

      setObservations((current) => ({ ...current, [questionText]: observation }));

      if (answers[questionText] === "sim" && observation.trim() === "") {
        setErrors((current) => ({
          ...current,
          [questionText]: "Observação é obrigatória",
        }));
        return;
      }

      setErrors((current) => {
        const nextErrors = { ...current };
        delete nextErrors[questionText];
        return nextErrors;
      });
      setCurrentQuestionIndex(currentIndex + 1);
    },
    [answers]
  );

  const handleVerifyQuestions = React.useCallback(
    (question: Question): boolean => {
      let response = true;

      if (question.answerValidate?.length > 0) {
        for (const rule of question.answerValidate) {
          if (typeof answers[rule[0]] !== "undefined" && rule[1] === "any") {
            response = true;
            break;
          }

          response = answers[rule[0]] === rule[1];
        }
      }

      return response;
    },
    [answers]
  );

  const handleNeurologicalConditionChange = React.useCallback(
    (name: string, isChecked: boolean) => {
      setSelectedNeurologicalConditions((current) => {
        const nextConditions = isChecked
          ? [...current, name]
          : current.filter((condition) => condition !== name);

        setHasNeurologicalCondition(
          nextConditions.length > 0 || otherNeurologicalCondition.length > 0
        );

        return nextConditions;
      });
    },
    [otherNeurologicalCondition.length]
  );

  React.useEffect(() => {
    setHasNeurologicalCondition(
      selectedNeurologicalConditions.length > 0 ||
        otherNeurologicalCondition.length > 0
    );
  }, [otherNeurologicalCondition, selectedNeurologicalConditions]);

  React.useEffect(() => {
    let ignore = false;

    const fetchBudgetItems = async () => {
      if (!attendanceBudgetId || !patientObj?.pac_id) {
        setBudgetItems([]);
        setBudgetLoadError("");
        setScheduleNextAppointment(false);
        return;
      }

      setLoadingBudgetItems(true);
      setBudgetLoadError("");

      try {
        const { data, status } = await loadOrcamento(
          user.data.id,
          attendanceBudgetId,
          patientObj.pac_id
        );

        if (!ignore && [200, 201].includes(status) && data.success && data.data) {
          setBudgetItems(data.data.itens || []);
          return;
        }

        if (!ignore) {
          setBudgetItems([]);
          setBudgetLoadError(
            "Não foi possível carregar os lançamentos do orçamento vinculado ao atendimento."
          );
        }
      } catch (error) {
        console.error("Erro ao carregar orçamento clínico do atendimento:", error);
        if (!ignore) {
          setBudgetItems([]);
          setBudgetLoadError(
            "Não foi possível carregar os lançamentos do orçamento vinculado ao atendimento."
          );
        }
      } finally {
        if (!ignore) {
          setLoadingBudgetItems(false);
        }
      }
    };

    fetchBudgetItems();

    return () => {
      ignore = true;
    };
  }, [attendanceBudgetId, patientObj?.pac_id, user.data.id]);

  React.useEffect(() => {
    if (
      !scheduleNextAppointment ||
      !selectedBudgetItem ||
      !attendanceBudgetId ||
      !currentDentistId ||
      patientObj?.future?.scheduling
    ) {
      setTimeTableData(null);
      setAvailableHours([]);
      setSelectedDate(null);
      setSelectedHour("");
      return;
    }

    let ignore = false;

    const fetchTimeTable = async () => {
      setLoadingTimeTable(true);

      try {
        const today = dayjs().format("YYYY-MM-DD");
        const { data, status } = await getTimeTable({
          id: user.data.id,
          date: today,
          interval: selectedInterval,
          separator: 0,
          idProcedure: selectedBudgetItem.procId,
          idDent: currentDentistId,
          idPac: patientObj.pac_id,
        });

        if (!ignore && [200, 201].includes(status) && data.success) {
          setTimeTableData(data.data);
          if (data.data.bestDay) {
            setSelectedDate(dayjs(data.data.bestDay, "YYYY-MM-DD"));
          }
          return;
        }

        if (!ignore) {
          toast.error(data.message || "Não foi possível buscar horários.");
          setTimeTableData(null);
          setAvailableHours([]);
        }
      } catch (error) {
        console.error("Erro ao buscar horários disponíveis:", error);
        if (!ignore) {
          toast.error("Não foi possível buscar horários para o próximo atendimento.");
          setTimeTableData(null);
          setAvailableHours([]);
        }
      } finally {
        if (!ignore) {
          setLoadingTimeTable(false);
        }
      }
    };

    fetchTimeTable();

    return () => {
      ignore = true;
    };
  }, [
    attendanceBudgetId,
    currentDentistId,
    patientObj?.future?.scheduling,
    patientObj?.pac_id,
    scheduleNextAppointment,
    selectedBudgetItem,
    selectedInterval,
    user.data.id,
  ]);

  React.useEffect(() => {
    if (!timeTableData || !selectedDate) {
      setAvailableHours([]);
      setSelectedHour("");
      return;
    }

    const formattedDate = selectedDate.format("MM/DD/YYYY");
    const matchingDate = Object.entries(timeTableData.availableDates).find(
      ([dateKey]) => convertDateToAmerican(dateKey) === formattedDate
    );

    if (!matchingDate) {
      setAvailableHours([]);
      setSelectedHour("");
      return;
    }

    const hours = matchingDate[1].map((hour) =>
      dayjs(`2023-01-01 ${hour}`).format("HH:mm")
    );

    setAvailableHours(hours);
    setSelectedHour((current) => (hours.includes(current) ? current : ""));
  }, [selectedDate, timeTableData]);

  React.useEffect(() => {
    setSelectedBudgetItemId((current) =>
      availableBudgetItems.some((item) => item.id === current) ? current : ""
    );
  }, [availableBudgetItems]);

  React.useEffect(() => {
    if (scheduleNextAppointment && availableBudgetItems.length === 0) {
      setScheduleNextAppointment(false);
    }
  }, [availableBudgetItems.length, scheduleNextAppointment]);

  React.useEffect(() => {
    if (!scheduleNextAppointment) {
      setSelectedBudgetItemId("");
      setSelectedDate(null);
      setSelectedHour("");
    }
  }, [scheduleNextAppointment]);

  const resizeSignatureCanvas = React.useCallback(
    (
      signatureRef: React.RefObject<SignatureCanvas>,
      containerRef: React.RefObject<HTMLDivElement>
    ) => {
      const canvas = signatureRef.current?.getCanvas();
      const containerWidth = containerRef.current?.clientWidth;

      if (!canvas || !containerWidth) return;

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = containerWidth * ratio;
      canvas.height = SIGNATURE_HEIGHT * ratio;

      const context = canvas.getContext("2d");
      context?.scale(ratio, ratio);
      signatureRef.current?.clear();
    },
    []
  );

  React.useEffect(() => {
    if ((hasAnamnesisStep && step !== 1) || typeof window === "undefined") {
      return;
    }

    const syncSignatureCanvases = () => {
      resizeSignatureCanvas(sigCanvasPatient, sigContainerPatient);
      resizeSignatureCanvas(sigCanvasDentist, sigContainerDentist);
    };

    syncSignatureCanvases();
    window.addEventListener("resize", syncSignatureCanvases);

    return () => {
      window.removeEventListener("resize", syncSignatureCanvases);
    };
  }, [hasAnamnesisStep, resizeSignatureCanvas, step]);

  const saveSignaturePatient = () => {
    if (!sigCanvasPatient.current) return;

    const dataUrl = sigCanvasPatient.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    setSignaturePatient(dataUrl);
  };

  const saveSignatureDentist = () => {
    if (!sigCanvasDentist.current) return;

    const dataUrl = sigCanvasDentist.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    setSignatureDoctor(dataUrl);
  };

  const clearSignaturePatient = () => {
    sigCanvasPatient.current?.clear();
    setSignaturePatient(null);
  };

  const clearSignatureDentist = () => {
    sigCanvasDentist.current?.clear();
    setSignatureDoctor(null);
  };

  const handlePrint = () => {
    setIsBtnFixedVisible(false);
    setTimeout(() => {
      window.print();
      setIsBtnFixedVisible(true);
    }, 0);
  };

  const handleReferralChoice = () => {
    setShowReferralDialog(false);
    setShowMandatoryReferral(true);
  };

  const handleReferralComplete = () => {
    setReferralCompleted(true);
    referralCompletedRef.current = true;
    toast.success("Encaminhamento realizado com sucesso! Clique em Finalizar para continuar.");
  };

  const handleCloseMandatoryReferral = () => {
    setShowMandatoryReferral(false);
    setPreSelectedProcedure(null);
  };

  const buildClinicalPayload = (): ISendSchedule => {
    const schedules =
      scheduleNextAppointment &&
      attendanceBudgetId &&
      !patientObj?.future?.scheduling &&
      selectedBudgetItem &&
      selectedDate &&
      selectedHour
        ? [
            {
              idPac: patientObj.pac_id,
              idDent: currentDentistId,
              date: `${selectedDate.format("YYYY-MM-DD")} ${selectedHour}`,
              interval: Number(selectedInterval),
              procedure: selectedBudgetItem.procId,
              idBudget: attendanceBudgetId,
              idLaunch: Number(selectedBudgetItem.id),
            },
          ]
        : [];

    const procedures = [
      {
        question: "Procedimento atendido",
        description: currentProcedureName,
      },
      {
        question: "Observação clínica",
        description: clinicalObservation.trim(),
      },
      ...(attendanceBudgetId
        ? [
            {
              question: "Orçamento clínico relacionado",
              description: `Orçamento #${attendanceBudgetId}`,
            },
          ]
        : []),
      ...(scheduleNextAppointment && selectedBudgetItem && selectedDate && selectedHour
        ? [
            {
              question: "Próximo atendimento agendado",
              description: `${selectedBudgetItem.procNome} em ${selectedDate.format(
                "DD/MM/YYYY"
              )} às ${selectedHour}`,
            },
          ]
        : []),
    ];

    return {
      id: user.data.id,
      idPac: patientObj.pac_id,
      idRecord: patientObj.record_id || 0,
      idSchedule: patientObj.id,
      idBudget: attendanceBudgetId,
      budget: 0,
      nextAppointment: [],
      odont: [],
      schedules,
      doc: {
        procedures,
        observations: `ATENDIMENTO CLÍNICO\n\nProcedimento atendido:\n${currentProcedureName}\n\nObservação clínica:\n${clinicalObservation.trim()}`,
        clinical: true,
      },
      signature: {
        pac:
          signaturePatient?.replace(/^data:image\/[a-z]+;base64,/, "") || "",
        dent:
          signatureDoctor?.replace(/^data:image\/[a-z]+;base64,/, "") || "",
      },
      hasFuture: patientObj?.future?.scheduling ? 1 : 0,
    };
  };

  const validateClinicalRecord = (): boolean => {
    if (showClinicalObservation && !clinicalObservation.trim()) {
      toast.error("Preencha a observação clínica.");
      return false;
    }

    if (!signaturePatient) {
      toast.error("Preencha a assinatura do paciente.");
      return false;
    }

    if (!signatureDoctor) {
      toast.error("Preencha a assinatura do dentista.");
      return false;
    }

    if (scheduleNextAppointment) {
      if (!attendanceBudgetId) {
        toast.error(
          "Este atendimento não possui orçamento clínico vinculado para gerar um novo agendamento."
        );
        return false;
      }

      if (availableBudgetItems.length === 0) {
        setScheduleNextAppointment(false);
        return true;
      }

      if (!selectedBudgetItem) {
        toast.error("Selecione o lançamento que será usado no próximo agendamento.");
        return false;
      }

      if (!selectedDate) {
        toast.error("Selecione a data do próximo atendimento.");
        return false;
      }

      if (!selectedHour) {
        toast.error("Selecione o horário do próximo atendimento.");
        return false;
      }
    }

    return true;
  };

  const navigateToHome = React.useCallback(() => {
    localStorage.removeItem("personAttendance");
    localStorage.removeItem("pendingAvaliation");
    navigate(ROUTER.HOME);
  }, [navigate]);

  const handleScheduleResponse = (responseData: any) => {
    const scheduled = responseData?.data?.scheduled;

    if (
      !scheduled ||
      (Array.isArray(scheduled) && scheduled.length === 0) ||
      (typeof scheduled === "object" && Object.keys(scheduled).length === 0)
    ) {
      navigateToHome();
      return;
    }

    const scheduledMessages =
      typeof scheduled === "object" && !Array.isArray(scheduled)
        ? Object.values(scheduled)
        : scheduled;

    if (Array.isArray(scheduledMessages) && scheduledMessages.length > 0) {
      toast.success(scheduledMessages.join(" | "));
    }

    navigateToHome();
  };

  const sendSimple = async () => {
    setLoading(true);

    try {
      if (Number(patientObj?.isOrto) === 1 && !referralCompletedRef.current) {
        const { status: checkStatus, data: checkData } =
          await checkReferralNeeded(patientObj.pac_id, user.data.id, patientObj.proc_id);

        if (
          [200, 201].includes(checkStatus) &&
          checkData.data.needsReferral
        ) {
          setReferralData({
            needsProfilaxia: checkData.data.needsProfilaxia,
            needsPanoramica: checkData.data.needsPanoramica,
            needsAvaliacao: checkData.data.needsAvaliacao,
            lastProfilaxiaDate: checkData.data.lastProfilaxiaDate,
            lastPanoramicaDate: checkData.data.lastPanoramicaDate,
          });
          setShowReferralDialog(true);
          setLoading(false);
          return;
        }
      }

      const { status, data } = await sendClinical({
        id: user.data.id,
        idPac: patientObj.pac_id,
        idSchedule: patientObj.id,
      });

      if ([200, 201].includes(status)) {
        toast.success(data.message);
        navigate(ROUTER.HOME);
        localStorage.removeItem("personAttendance");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Erro ao enviar:", error);
      toast.error("Ocorreu um erro ao tentar enviar.");
    } finally {
      setLoading(false);
    }
  };

  const send = async (skipDay20Check = false) => {
    if (!validateClinicalRecord()) return;

    if (
      !skipDay20Check &&
      scheduleNextAppointment &&
      !patientObj?.future?.scheduling &&
      selectedDate &&
      selectedDate.date() > 20
    ) {
      setOpenAfterDay20(true);
      return;
    }

    setLoading(true);

    try {
      if (Number(patientObj?.isOrto) === 1 && !referralCompletedRef.current) {
        const { status: checkStatus, data: checkData } =
          await checkReferralNeeded(patientObj.pac_id, user.data.id, patientObj.proc_id);

        if (
          [200, 201].includes(checkStatus) &&
          checkData.data.needsReferral
        ) {
          setReferralData({
            needsProfilaxia: checkData.data.needsProfilaxia,
            needsPanoramica: checkData.data.needsPanoramica,
            needsAvaliacao: checkData.data.needsAvaliacao,
            lastProfilaxiaDate: checkData.data.lastProfilaxiaDate,
            lastPanoramicaDate: checkData.data.lastPanoramicaDate,
          });
          setShowReferralDialog(true);
          return;
        }
      }

      if (hasAnamnesisStep) {
        const payloadAnamnese: IAnamneseData = {
          id: user.data.id,
          idPac: patientObj.pac_id,
          data: anamnesisPayload,
        };

        const { status: anamnesisStatus, data: anamnesisData } =
          await postAnamnese(payloadAnamnese);

        if (![200, 201].includes(anamnesisStatus)) {
          toast.error(anamnesisData.message || "Não foi possível salvar a anamnese.");
          return;
        }
      }

      const payload = buildClinicalPayload();
      const { status, data } = await sendSchedule(payload);

      if ([200, 201].includes(status)) {
        toast.success(data.message || "Ficha clínica salva com sucesso!");
        handleScheduleResponse(data);
        return;
      }

      toast.error(data?.message || "Não foi possível salvar a ficha clínica.");
    } catch (error) {
      console.error("Erro ao finalizar atendimento clínico:", error);
      toast.error("Ocorreu um erro ao tentar finalizar o atendimento clínico.");
    } finally {
      setLoading(false);
    }
  };

  const renderAnamnesisQuestion = (
    question: Question,
    index: number,
    show: boolean
  ) => {
    if (!show || index > currentQuestionIndex) return null;

    return (
      <AnamneseStyled.BoxDescription key={question.questionText}>
        <Typography variant="h5">{question.questionText}</Typography>

        <AnamneseStyled.BoxButtons>
          <Button
            variant={
              answers[question.questionText] === "não" ? "contained" : "outlined"
            }
            onClick={() =>
              handleAnswerChange(
                question.questionText,
                "não",
                typeof question.answerValidate === "undefined"
              )
            }
          >
            NÃO
          </Button>
          <Button
            variant={
              answers[question.questionText] === "sim" ? "contained" : "outlined"
            }
            onClick={() =>
              handleAnswerChange(
                question.questionText,
                "sim",
                typeof question.answerValidate === "undefined"
              )
            }
          >
            SIM
          </Button>
        </AnamneseStyled.BoxButtons>

        {answers[question.questionText] === "sim" &&
          question.questionText !== "A recuperação aconteceu como esperado?" &&
          question.questionText !== "A recuperação ocorreu como esperado?" && (
            <TextField
              label="Observação"
              value={observations[question.questionText] || ""}
              onChange={(event) =>
                handleObservationChange(question.questionText, event.target.value)
              }
              fullWidth
              margin="normal"
              error={!!errors[question.questionText]}
              helperText={errors[question.questionText]}
            />
          )}

        {answers[question.questionText] === "não" &&
          (question.questionText === "A recuperação aconteceu como esperado?" ||
            question.questionText === "A recuperação ocorreu como esperado?") && (
            <TextField
              label="Observação"
              value={observations[question.questionText] || ""}
              onChange={(event) =>
                handleObservationChange(question.questionText, event.target.value)
              }
              fullWidth
              margin="normal"
              error={!!errors[question.questionText]}
              helperText={errors[question.questionText]}
            />
          )}
      </AnamneseStyled.BoxDescription>
    );
  };

  const renderAnamnesisStep = () => {
    return (
      <AnamneseStyled.Box>
        <Typography variant="h5">Anamnese</Typography>

        <AnamneseStyled.AriaBox>
          {ANAMNESIS_OPTIONS.map((item) => (
            <FormControlLabel
              key={item.identify}
              control={
                <Checkbox
                  checked={selectedOptions.includes(item)}
                  onChange={() => handleOptionChange(item)}
                />
              }
              label={item.label}
            />
          ))}
        </AnamneseStyled.AriaBox>

        <AnamneseStyled.NeurologicalSection>
          <AnamneseStyled.NeurologicalHeader>
            <MdWarning size={20} color="#f57c00" />
            <h5>Condições Neurológicas e Transtornos</h5>
          </AnamneseStyled.NeurologicalHeader>

          <Typography
            variant="body2"
            sx={{ color: "#666", marginBottom: "10px" }}
          >
            Selecione as condições neurológicas ou transtornos do
            neurodesenvolvimento do paciente.
          </Typography>

          <Collapse in={hasNeurologicalCondition}>
            <Alert severity="warning" sx={{ marginBottom: "15px" }}>
              Paciente com necessidades especiais. Considere cuidados
              diferenciados durante o atendimento.
            </Alert>
          </Collapse>

          <AnamneseStyled.NeurologicalGrid>
            {NeurologicalConditions.map((condition) => (
              <FormControlLabel
                key={condition.id}
                control={
                  <Checkbox
                    checked={selectedNeurologicalConditions.includes(
                      condition.name
                    )}
                    onChange={(event) =>
                      handleNeurologicalConditionChange(
                        condition.name,
                        event.target.checked
                      )
                    }
                  />
                }
                label={condition.name}
              />
            ))}
          </AnamneseStyled.NeurologicalGrid>

          <TextField
            label="Outras condições"
            placeholder="Digite outras condições neurológicas não listadas"
            onChange={(event) => setOtherNeurologicalCondition(event.target.value)}
            value={otherNeurologicalCondition}
            fullWidth
            multiline
            rows={2}
            sx={{ marginTop: "15px" }}
          />
        </AnamneseStyled.NeurologicalSection>

        {questions.map((question, index) =>
          renderAnamnesisQuestion(
            question,
            index,
            handleVerifyQuestions(question)
          )
        )}

        {currentQuestionIndex >= questions.length && (
          <AnamneseStyled.BtnFixed>
            <Button fullWidth onClick={() => navigate(ROUTER.HOME)} variant="outlined">
              Cancelar
            </Button>
            <Button fullWidth onClick={() => setStep(1)}>
              Continuar
            </Button>
          </AnamneseStyled.BtnFixed>
        )}
      </AnamneseStyled.Box>
    );
  };

  const renderBudgetItemSelector = () => {
    if (!attendanceBudgetId) {
      return (
        <RecordStyled.BoxClinical>
          <Typography variant="h5">Orçamento Clínico</Typography>
          <Typography>
            Este atendimento não possui orçamento clínico vinculado ao
            lançamento atual. A ficha será salva sem orçamento.
          </Typography>
        </RecordStyled.BoxClinical>
      );
    }

    return (
      <RecordStyled.BoxClinical>
        <Typography variant="h5">Orçamento Clínico</Typography>
        <Typography>
          Orçamento vinculado ao atendimento: <strong>#{attendanceBudgetId}</strong>
        </Typography>

        {budgetLoadError && (
          <Alert severity="warning">{budgetLoadError}</Alert>
        )}

        {loadingBudgetItems ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={24} />
          </Box>
        ) : patientObj?.future?.scheduling ? (
          <Alert severity="info">
            {patientObj.future.message ||
              "O paciente já possui um próximo agendamento."}
          </Alert>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={scheduleNextAppointment}
                  disabled={availableBudgetItems.length === 0}
                  onChange={(event) =>
                    setScheduleNextAppointment(event.target.checked)
                  }
                />
              }
              label="Agendar próximo atendimento usando um lançamento deste orçamento"
            />

            {availableBudgetItems.length === 0 ? (
              <Alert severity="info">
                Não existem lançamentos disponíveis para um novo agendamento
                neste orçamento.
              </Alert>
            ) : scheduleNextAppointment && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {budgetItems.map((item) => {
                    const isCurrentLaunch =
                      Number(patientObj?.clinical_launch_id || 0) > 0 &&
                      Number(item.id) === Number(patientObj?.clinical_launch_id);
                    const completed = isLaunchCompleted(item);
                    const scheduled = isLaunchScheduled(item);
                    const blocked =
                      completed || scheduled || isCurrentLaunch;

                    return (
                      <Box
                        key={item.id}
                        onClick={() => {
                          if (!blocked) {
                            setSelectedBudgetItemId(item.id);
                          }
                        }}
                        sx={{
                          border: "1px solid #eeeff1",
                          backgroundColor:
                            selectedBudgetItemId === item.id
                              ? "#e8f2ff"
                              : "#f7f8fa",
                          padding: "12px 15px",
                          cursor: blocked ? "not-allowed" : "pointer",
                          opacity: blocked ? 0.65 : 1,
                        }}
                      >
                        <Typography sx={{ fontWeight: "bold" }}>
                          {item.procNome}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {getBudgetItemDescription(item) || "Sem detalhes adicionais"}
                        </Typography>
                        {blocked && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#d32f2f", fontWeight: "bold" }}
                          >
                            {completed
                              ? "Lançamento já realizado"
                              : scheduled
                              ? "Lançamento já vinculado a outro agendamento"
                              : "Lançamento do atendimento atual"}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {selectedBudgetItem && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel id="clinical-interval-label">Duração</InputLabel>
                      <Select
                        labelId="clinical-interval-label"
                        value={selectedInterval}
                        label="Duração"
                        onChange={(event) =>
                          setSelectedInterval(String(event.target.value))
                        }
                      >
                        {APPOINTMENT_INTERVALS.map((interval) => (
                          <MenuItem key={interval} value={String(interval)}>
                            {interval} minutos
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {loadingTimeTable ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : timeTableData ? (
                      <>
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale="pt-br"
                        >
                          <DatePicker
                            label="Data do próximo atendimento"
                            value={selectedDate}
                            onChange={(value) => setSelectedDate(value)}
                            format="DD/MM/YYYY"
                            minDate={dayjs()}
                            shouldDisableDate={(date) => {
                              const availableDateSet = new Set<string>(
                                Object.keys(timeTableData.availableDates).map(
                                  (dateKey) => convertDateToAmerican(dateKey)
                                )
                              );

                              return !availableDateSet.has(
                                date.format("MM/DD/YYYY")
                              );
                            }}
                          />
                        </LocalizationProvider>

                        <FormControl fullWidth>
                          <InputLabel id="clinical-hour-label">Horário</InputLabel>
                          <Select
                            labelId="clinical-hour-label"
                            value={selectedHour}
                            label="Horário"
                            onChange={(event) =>
                              setSelectedHour(String(event.target.value))
                            }
                          >
                            {availableHours.map((hour) => (
                              <MenuItem key={hour} value={hour}>
                                {hour}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </>
                    ) : (
                      <Alert severity="warning">
                        Não foi possível carregar horários para este lançamento.
                      </Alert>
                    )}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </RecordStyled.BoxClinical>
    );
  };

  const renderClinicalRecordStep = () => {
    return (
      <RecordStyled.Box className="printable">
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "flex-end",
            marginTop: "15px",
          }}
        >
          <RecordStyled.BoxSave onClick={handlePrint} className="no-print">
            Imprimir
          </RecordStyled.BoxSave>
        </Box>

        <RecordStyled.BoxClinical>
          <Typography variant="h5">Atendimento Clínico</Typography>

          <Box>
            <Typography sx={{ fontWeight: "bold" }}>
              Procedimento atendido
            </Typography>
            <Typography>{currentProcedureName}</Typography>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: "bold" }}>
              Data do atendimento
            </Typography>
            <Typography>{currentScheduleDate || "Não informado"}</Typography>
          </Box>

          {showClinicalObservation && (
            <TextField
              label="Observação clínica"
              value={clinicalObservation}
              onChange={(event) => setClinicalObservation(event.target.value)}
              fullWidth
              multiline
              minRows={5}
            />
          )}
        </RecordStyled.BoxClinical>

        {renderBudgetItemSelector()}

        <RecordStyled.BoxClinicalSignature className="boxValue">
          <Typography
            variant="h5"
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            Assinatura do Paciente
            <Button onClick={clearSignaturePatient} className="no-print">
              Limpar
            </Button>
          </Typography>
          <div
            className="boxSignature"
            ref={sigContainerPatient}
            style={{ minHeight: SIGNATURE_HEIGHT }}
          >
            <SignatureCanvas
              onEnd={saveSignaturePatient}
              ref={sigCanvasPatient}
              penColor="black"
              canvasProps={{
                className: "sigCanvas",
                style: { width: "100%", height: `${SIGNATURE_HEIGHT}px` },
              }}
            />
          </div>
        </RecordStyled.BoxClinicalSignature>

        <RecordStyled.BoxClinicalSignature className="boxValue">
          <Typography
            variant="h5"
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            Assinatura do Dentista
            <Button onClick={clearSignatureDentist} className="no-print">
              Limpar
            </Button>
          </Typography>
          <div
            className="boxSignature"
            ref={sigContainerDentist}
            style={{ minHeight: SIGNATURE_HEIGHT }}
          >
            <SignatureCanvas
              onEnd={saveSignatureDentist}
              ref={sigCanvasDentist}
              penColor="black"
              canvasProps={{
                className: "sigCanvas",
                style: { width: "100%", height: `${SIGNATURE_HEIGHT}px` },
              }}
            />
          </div>
        </RecordStyled.BoxClinicalSignature>

        {isBtnFixedVisible ? (
          <RecordStyled.BtnFixed className="no-print">
            {hasAnamnesisStep ? (
              <Button fullWidth onClick={() => setStep(0)} variant="outlined">
                Voltar
              </Button>
            ) : (
              <Button fullWidth onClick={() => navigate(ROUTER.HOME)} variant="outlined">
                Cancelar
              </Button>
            )}
            <Button fullWidth onClick={() => send()} disabled={loading}>
              {loading ? <CircularProgress color="inherit" /> : "Finalizar"}
            </Button>
          </RecordStyled.BtnFixed>
        ) : (
          ""
        )}
      </RecordStyled.Box>
    );
  };

  return (
    <>
      <HeadConsult title="Clínico Geral" />

      {hasBudgetFlow ? (
        hasAnamnesisStep && step === 0
          ? renderAnamnesisStep()
          : renderClinicalRecordStep()
      ) : (
        <Box
          sx={{
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            marginTop: "4rem",
          }}
        >
          <Button fullWidth onClick={sendSimple} disabled={loading}>
            {loading ? <CircularProgress color="inherit" /> : "Finalizar"}
          </Button>
        </Box>
      )}

      <Dialog
        open={showReferralDialog}
        onClose={() => {}}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          Encaminhamento Obrigatório
        </DialogTitle>
        <DialogContent>
          {(referralData?.needsProfilaxia || referralData?.needsPanoramica) && (
            <Typography sx={{ textAlign: "center", mb: 2 }}>
              O paciente necessita realizar encaminhamento para os seguintes
              procedimentos:
            </Typography>
          )}

          {referralData && (
            <Box sx={{ mb: 2 }}>
              {referralData.needsProfilaxia && (
                <Typography
                  sx={{ textAlign: "center", color: "#d32f2f", mb: 1 }}
                >
                  • Profilaxia{" "}
                  {referralData.lastProfilaxiaDate
                    ? `(última: ${referralData.lastProfilaxiaDate})`
                    : "(nunca realizada)"}
                </Typography>
              )}
              {referralData.needsPanoramica && (
                <Typography sx={{ textAlign: "center", color: "#d32f2f", mb: 1 }}>
                  • Raio-X{" "}
                  {referralData.lastPanoramicaDate
                    ? `(última: ${referralData.lastPanoramicaDate})`
                    : "(nunca realizada)"}
                </Typography>
              )}
              {referralData.needsAvaliacao && (
                <Typography sx={{ textAlign: "center", color: "#d32f2f", mb: 1 }}>
                  • Avaliação Clínica (obrigatória para este procedimento)
                </Typography>
              )}
            </Box>
          )}

          <Typography
            sx={{ textAlign: "center", color: "#666", fontSize: "14px" }}
          >
            Para encerrar o atendimento, é necessário realizar o(s)
            encaminhamento(s) pendente(s).
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ flexDirection: "column", gap: 1, padding: "16px" }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleReferralChoice()}
            sx={{ backgroundColor: "#007DED" }}
          >
            Realizar Encaminhamento
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setShowReferralDialog(false)}
            sx={{ marginTop: 1 }}
          >
            Voltar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAfterDay20} onClose={() => setOpenAfterDay20(false)}>
        <DialogTitle>Agendamento após o dia 20</DialogTitle>
        <DialogContent>
          <Typography>
            Recomendamos que o agendamento seja feito para datas que antecedem
            o dia 20 de cada mês. Deseja continuar mesmo assim?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenAfterDay20(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setOpenAfterDay20(false);
              send(true);
            }}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <ReferralReferrals
        open={showMandatoryReferral}
        close={handleCloseMandatoryReferral}
        pacId={patientObj?.pac_id}
        pacFantasy={patientObj?.pac_id_fant?.toString() || ""}
        onCompleteOrto={handleReferralComplete}
        emailPatient={patientObj?.pac_email}
        preSelectedProcedure={preSelectedProcedure}
        requiredProcedures={
          referralData
            ? [
                ...(referralData.needsProfilaxia ? ["profilaxia" as const] : []),
                ...(referralData.needsPanoramica ? ["raio_x" as const] : []),
                ...(referralData.needsAvaliacao ? ["avaliacao" as const] : []),
              ]
            : []
        }
      />
    </>
  );
};

export { Finalize };
